import { useCallback, useEffect, useRef, useState } from 'react';
import { parseIMNotificationSettings, type IMNotificationPlatform, type IMNotificationSettings } from '../../shared/imNotifications';
import { i18nService } from '../services/i18n';

/** 设置按账号/实例/平台隔离；写入成功才替换选择，迟到请求不更新新页面。 */
export function useIMNotifications(instanceId: string, platform: IMNotificationPlatform) {
  const [snapshot, setSnapshot] = useState<IMNotificationSettings | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const epoch = useRef(0), pending = useRef<object | null>(null);

  /** 参数缺省代表读取，null代表关闭；同步锁禁止重复保存。 */
  const request = useCallback(async (selectTarget?: string | null) => {
    if (pending.current) return;
    const operation = {}, generation = epoch.current; pending.current = operation; setBusy(true); setError('');
    try {
      const api = window.electron.im;
      if (!api.getNotificationSettings || !api.setNotificationTarget) throw new Error('unsupported');
      const result = selectTarget === undefined ? await api.getNotificationSettings(instanceId, platform) : await api.setNotificationTarget(instanceId, platform, selectTarget);
      if (generation === epoch.current) setSnapshot(parseIMNotificationSettings(result));
    } catch {
      if (generation === epoch.current) setError(i18nService.t('imNotificationRequestFailed'));
    } finally {
      if (pending.current === operation) { pending.current = null; setBusy(false); }
    }
  }, [instanceId, platform]);

  useEffect(() => {
    /** 身份切换先撤销旧响应，再由登录状态事件加载当前账号。 */
    const clear = () => { epoch.current++; pending.current = null; setSnapshot(null); setError(''); setBusy(false); };
    const refresh = () => { void request(); };
    clear(); refresh();
    window.addEventListener('auth:identityChanging', clear);
    window.addEventListener('auth:stateChanged', refresh);
    window.addEventListener('focus', refresh);
    // 网关入站和任务通知状态均已由WebSocket按账号过滤；页面打开时同步最新候选与回执。
    const subscriptions = ['im.message.received', 'scheduledTask.notification'].map(channel => window.electron.ipcRenderer?.on(channel, refresh));
    return () => {
      epoch.current++; pending.current = null;
      window.removeEventListener('auth:identityChanging', clear);
      window.removeEventListener('auth:stateChanged', refresh);
      window.removeEventListener('focus', refresh);
      subscriptions.forEach(unsubscribe => unsubscribe?.());
    };
  }, [request]);
  return { snapshot, error, busy, refresh: () => request(), select: (id: string | null) => request(id) };
}
